// Layer 3 (Transformation): pure reads for the case drawer — signal, thread, rival, effectiveness.

import type { Case, CauseCategory, FMEAUpdateReview, RecommendedAction } from '@/versions/v1/schema/case';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { isRuleLive } from '@/versions/v1/shared/AdvisoryEngine';
import type { EffectivenessRead } from '@/versions/v1/schema/case';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import { effectivenessForAction } from '@/versions/v1/shared/EffectivenessEngine';
import { liveRuleSet, matchedRuleIdForCase, ruleOutcome, RULE_OUTCOME_LABEL } from '@/versions/v1/shared/RuleSetEngine';
import { threadForCase, threadSummaries, variableIdForCase } from '@/versions/v1/shared/threads';
import {
  ADVISORY_RULES,
  VARIATION_EVENTS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { DIAGNOSTIC_RULES } from '@/versions/v1/shared/bunker-hill-seed';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { MONITORED_VARIABLES, TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { STREAMS } from '@/versions/v1/shared/bunker-hill-seed';
import { plantNode, subprocessForNodeId } from '@/versions/v1/shared/bunker-hill-plant-nodes';
import { controlChart, formatSpcValue, type ControlChartViewModel } from '@/versions/v1/shared/SpcEngine';
import { perturbedSeriesIds, SCENARIOS } from '@/versions/v1/shared/perturbations';
import { ruleById } from '@/versions/v1/shared/diagnostic-rule.transform';
import { metDiagnosticsCauseHref } from '@/versions/v1/shared/met-diagnostics-cause.derive';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { seatLabelById } from '@/versions/v1/shared/org';
import { formatClockShort, formatElapsedMinutes } from '@/versions/v1/shared/clock.derive';
import { ladderRead, type LadderTierView } from '@/versions/v1/shared/ladder.transform';
import { roleShortLabel } from '@/versions/v1/shared/org';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { CASE_OUTCOMES, CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { znVariationDescription, znVariationMagnitude } from '@/versions/v1/shared/recovery-vocab.derive';
import { outcomeForCase, reviewsForCase } from './Scene.transform';

const READING_BY_ID = new Map(READINGS.map((row) => [row.id, row]));

const RULE_BY_ID = new Map([...DIAGNOSTIC_RULES, ...ADVISORY_RULES].map((row) => [row.id, row]));

export type DrawerThreadLine = {
  label: string;
  openCount: number;
};

export type DrawerRivalLine = {
  label: string;
  pct: number;
};

export type DrawerEffectivenessLine = {
  state: EffectivenessRead['state'];
  signalLabel: string;
  successDirection?: string;
  observedTrend: string;
  since: string;
};

function variableIdFromVariationEvent(eventId: string): string | undefined {
  if (eventId.startsWith('ex-')) {
    const match = MONITORED_VARIABLES.find((row) => eventId.startsWith(`ex-${row.id}-`));
    if (match) return match.id;
    return undefined;
  }
  const seeded = VARIATION_EVENTS.find((row) => row.id === eventId);
  if (!seeded) return undefined;
  if (seeded.variableId) return seeded.variableId;
  return undefined;
}

export type DrawerSignalSection =
  | { kind: 'chart'; chart: ControlChartViewModel }
  | { kind: 'honest'; message: string };

/** Instrument and loop tags for a case's Plant data cell, resolved through its
 *  seeded monitored variable and the shared tag map. */
export function plantDataTagsForCase(caseItem: Case): readonly string[] {
  const variableId = variableIdForCase(caseItem);
  const variable = MONITORED_VARIABLES.find((row) => row.id === variableId);
  if (!variable) return [];
  const mapping = TAG_MAPPING.filter((row) => row.seriesId === variable.seriesId);
  return [...new Set(mapping.flatMap((row) =>
    [row.instrumentTag, row.loopTag].filter((tag): tag is string => Boolean(tag))))];
}

function signalHonestMessage(caseItem: Case): string | undefined {
  if (!caseItem.variationEventId) return undefined;
  const seeded = VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId);
  if (!seeded || seeded.variableId) return undefined;
  const reading = READINGS.find((row) => row.id === 'rd-spec-pb-as');
  const label = reading?.label ?? 'As in Pb conc';
  return `No series for ${label} at this seed · lab spec only`;
}

function signalVariableIdForCase(
  caseItem: Case,
  state: SessionState,
  diagnosis: Diagnosis,
): string | undefined {
  if (caseItem.id === 'case-cyc-wear') return 'mv-cyc-p80-6w';
  if (!caseItem.variationEventId) return undefined;
  const seeded = VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId);
  const base = variableIdFromVariationEvent(caseItem.variationEventId);
  if (
    seeded?.subprocessId === 'sp-grind' &&
    state.scenarios.includes('media-depletion')
  ) {
    const media = diagnosis.candidates.find((row) => row.rule.id === 'dr-media');
    if (media) return media.excursion.variableId;
  }
  return base;
}

export function signalSectionForCase(
  caseItem: Case,
  state: SessionState,
  diagnosis?: Diagnosis,
): DrawerSignalSection | undefined {
  const honest = signalHonestMessage(caseItem);
  if (honest) return { kind: 'honest', message: honest };
  const chart = signalChartForCase(caseItem, state, diagnosis);
  if (!chart) return undefined;
  const variable = MONITORED_VARIABLES.find((row) => row.id === chart.variableId);
  if (variable && perturbedSeriesIds(state).has(variable.seriesId)) {
    return {
      kind: 'chart',
      chart: { ...chart, readingLabel: `${chart.readingLabel} [syn]` },
    };
  }
  return { kind: 'chart', chart };
}

export function signalChartForCase(
  caseItem: Case,
  state: SessionState,
  diagnosis?: Diagnosis,
) {
  if (!caseItem.variationEventId) return undefined;
  const dx = diagnosis ?? diagnose(state);
  const variableId = signalVariableIdForCase(caseItem, state, dx);
  if (!variableId) return undefined;
  try {
    return controlChart(variableId, state);
  } catch {
    return undefined;
  }
}

// `ve-p80` (case-p80 / GRIND-P80) is the only variation event with a
// prose magnitude line that names a P80 + specific-energy pair. Under
// `media-depletion` the case's own signal switches to `dr-media`'s
// mill-power-ratio excursion (`signalVariableIdForCase`); the magnitude
// line above the chart must read through the same scenario-aware path
// instead of the static seed string, or the chart and the number above
// it contradict each other.
const MEDIA_MAGNITUDE_PATTERN =
  /^P80 (\S+) µm (vs \S+ µm design) · (\S+) (vs \S+ kWh\/t)$/;

function mediaDepletionP80SetValue(): number | undefined {
  const bundle = SCENARIOS.find((s) => s.id === 'media-depletion');
  const leg = bundle?.perturbations.find(
    (p): p is Extract<(typeof bundle)['perturbations'][number], { kind: 'set' }> =>
      p.kind === 'set' && p.seriesId === 'rs-sa01-p80',
  );
  return leg?.value;
}

/**
 * `ve-p80`'s static seed magnitude, read through the scenario cursor: under
 * `media-depletion` both figures come from the perturbed series — the P80
 * reading from the scenario bundle's own `set rs-sa01-p80` leg (the shift-
 * sampled series has no point past the leg's start yet at this cursor) and
 * the specific-energy reading from `mv-jt-023`'s live chart value — both
 * tagged `[syn]`. At default (no scenario) the seed string passes through
 * unchanged.
 */
export function variationDescriptionForCase(caseItem: Case, description: string, state: SessionState): string {
  if (caseItem.id === 'case-zn-recovery') return znVariationDescription(state);
  return description;
}

export function variationMagnitudeForCase(
  caseItem: Case,
  magnitude: string,
  state: SessionState,
): string {
  if (caseItem.id === 'case-zn-recovery') return znVariationMagnitude(state);
  if (caseItem.variationEventId !== 've-p80') return magnitude;
  if (!state.scenarios.includes('media-depletion')) return magnitude;
  const match = MEDIA_MAGNITUDE_PATTERN.exec(magnitude);
  if (!match) return magnitude;
  const [, , designP80, , designEnergy] = match;
  const p80Value = mediaDepletionP80SetValue();
  const jtReading = READING_BY_ID.get('rd-jt023-kwpt');
  let energyValue: number | undefined;
  try {
    const jtChart = controlChart('mv-jt-023', state);
    energyValue = jtChart.points[jtChart.points.length - 1]?.value;
  } catch {
    energyValue = undefined;
  }
  if (p80Value === undefined || energyValue === undefined || !jtReading) return magnitude;
  const energyFormatted = formatSpcValue(jtReading, energyValue);
  return `P80 ${p80Value} µm ${designP80} [syn] · Mill specific energy ${energyFormatted} ${designEnergy} [syn]`;
}

function threadRootLabel(thread: { title: string; rootNodeId?: string }, summaries: ReturnType<typeof threadSummaries>): string {
  const summary = summaries.find((row) => row.id === (thread as { id?: string }).id);
  if (summary) return summary.label;
  const slash = thread.title.indexOf(' / ');
  if (slash > 0) return thread.title.slice(0, slash);
  const arrow = thread.title.indexOf(' → ');
  if (arrow > 0) return thread.title.slice(0, arrow);
  return thread.title;
}

export function drawerThreadLine(
  caseItem: Case,
  cases: readonly Case[],
  diagnosis: Diagnosis,
): DrawerThreadLine | undefined {
  const thread = threadForCase(caseItem.id, cases, diagnosis);
  if (!thread) return undefined;
  const summaries = threadSummaries(cases, diagnosis);
  const summary = summaries.find((row) => row.id === thread.id);
  const openCount =
    summary?.openCount ??
    thread.caseIds.filter((id) => {
      const row = cases.find((c) => c.id === id);
      return row && !['closed', 'approved', 'rejected'].includes(row.state);
    }).length;
  return { label: summary?.label ?? threadRootLabel(thread, summaries), openCount };
}

function rivalForCase(
  caseItem: Case,
  diagnosis: Diagnosis,
): DrawerRivalLine | undefined {
  const variableId = caseItem.variationEventId
    ? variableIdFromVariationEvent(caseItem.variationEventId)
    : caseItem.id.startsWith('case-mv-')
      ? caseItem.id.replace(/^case-/, '')
      : undefined;
  const candidate = diagnosis.candidates.find((row) => {
    if (variableId) return row.excursion.variableId === variableId;
    return false;
  });
  if (!candidate?.rule.rival || candidate.rivalScore <= 0) return undefined;
  const rivalRule = RULE_BY_ID.get(candidate.rule.rival);
  if (!rivalRule) return undefined;
  return {
    label: rivalRule.diagnosis,
    pct: Math.round(candidate.rivalScore * 100),
  };
}

export function drawerRivalLine(caseItem: Case, diagnosis: Diagnosis) {
  return rivalForCase(caseItem, diagnosis);
}

function directionMeansWorked(
  excursionDirection: 'up' | 'down' | undefined,
): string | undefined {
  if (excursionDirection === 'up') return 'falling is worked';
  if (excursionDirection === 'down') return 'rising is worked';
  return undefined;
}

function observedTrendLabel(read: EffectivenessRead): string {
  if (read.sentence.includes('no improvement since')) return 'no improvement';
  const trend = read.after?.trend ?? read.before?.trend;
  if (trend === 'rising') return 'rising';
  if (trend === 'falling') return 'falling';
  return 'stable';
}

export function effectivenessLineForAction(
  caseItem: Case,
  action: RecommendedAction,
  state: SessionState,
  advisory: AdvisorySessionState,
  diagnosis: Diagnosis,
  actions: readonly RecommendedAction[],
): DrawerEffectivenessLine | undefined {
  if (action.status !== 'approved' && action.status !== 'done') return undefined;
  const read = effectivenessForAction(action, caseItem, state, advisory, diagnosis, actions);
  if (!read || read.state === 'awaiting-action' || read.state === 'unobservable') return undefined;
  const observedTrend = observedTrendLabel(read);
  const excursionDirection =
    read.variableId
      ? diagnosis.candidates.find((row) => row.excursion.variableId === read.variableId)?.excursion
          .direction
      : undefined;
  return {
    state: read.state,
    signalLabel: read.label,
    successDirection:
      observedTrend === 'stable' ? undefined : directionMeansWorked(excursionDirection),
    observedTrend,
    since: read.since ?? '—',
  };
}

export type ActionDecisionLine = {
  /** "awaiting decision" | "approved" | "rejected" | "implemented" — absence
   *  (undecided) always reads as its own label, never blank (paul-feedback.A8
   *  hard rule 7). */
  statusLabel: string;
  /** "<seat label> · <decision timestamp>" — omitted when undecided. */
  decisionDetail?: string;
  /** Refusal reason — only present when rejected. */
  reason?: string;
  /** Implementation timestamp — a separate stamp from decision, never summed
   *  or substituted with it (hard rule 3). */
  implementedAt?: string;
};

/** Decision-record projection for one action — Layer 3, no React. */
export function actionDecisionLine(action: RecommendedAction): ActionDecisionLine {
  if (!action.decision) return { statusLabel: 'awaiting decision' };
  const at = action.decision.at;
  const stamp = at.includes('T') ? formatClockShort(at as SeriesTime) : at;
  const decisionDetail = `${seatLabelById(action.decision.seat)} · ${stamp}`;
  if (action.status === 'rejected') {
    return { statusLabel: 'rejected', decisionDetail, reason: action.decision.reason };
  }
  if (action.status === 'done') {
    const implementedAt = action.implementedAt;
    const implementedStamp =
      implementedAt && implementedAt.includes('T')
        ? formatClockShort(implementedAt as SeriesTime)
        : implementedAt;
    return { statusLabel: 'implemented', decisionDetail, implementedAt: implementedStamp };
  }
  return { statusLabel: 'approved', decisionDetail };
}

export function matchedRuleCauseCategory(ruleId: string): CauseCategory | undefined {
  return ruleById(ruleId)?.causeCategory ?? RULE_BY_ID.get(ruleId)?.causeCategory;
}

export function matchedRuleOutcomeLabel(
  caseItem: Case,
  diagnosis?: Diagnosis,
): string | undefined {
  const ruleId = matchedRuleIdForCase(caseItem, diagnosis);
  if (!ruleId) return undefined;
  const rule = ruleById(ruleId);
  if (!rule) return undefined;
  const outcome = ruleOutcome(rule);
  return outcome ? RULE_OUTCOME_LABEL[outcome] : undefined;
}

export function drawerLadderSection(
  caseItem: Case,
  cursor: SeriesTime,
): { tiers: readonly LadderTierView[] } | null {
  const read = ladderRead(caseItem, cursor);
  if (!read) return null;
  return { tiers: read.tiers };
}

function tierStatusLabel(tier: LadderTierView): string {
  const role = roleShortLabel(tier.role);
  const limit = formatElapsedMinutes(tier.timeLimitMinutes);
  if (tier.status === 'done') {
    const overdue =
      tier.overdueMinutes && tier.overdueMinutes > 0
        ? ` · overdue by ${formatElapsedMinutes(tier.overdueMinutes)}`
        : '';
    return `done · ${role} · ${tier.instruction} · ${limit}${overdue}`;
  }
  if (tier.status === 'current') {
    const escalate = tier.escalatesTo ? ` → ${roleShortLabel(tier.escalatesTo)}` : '';
    return `current · ${role} · ${tier.instruction} · ${limit}${escalate}`;
  }
  if (tier.status === 'next') {
    const escalate = tier.escalatesTo ? ` → ${roleShortLabel(tier.escalatesTo)}` : '';
    return `next · ${role} · ${tier.instruction} · ${limit}${escalate}`;
  }
  return `pending · ${role} · ${tier.instruction} · ${limit}`;
}

export function drawerLadderTierLines(tiers: readonly LadderTierView[]): string[] {
  return tiers.map(tierStatusLabel);
}

export function matchedRuleDiagnosis(ruleId: string): string | undefined {
  return ruleById(ruleId)?.diagnosis;
}

export function matchedRuleCauseHref(slug: string, ruleId: string): string | undefined {
  const rule = ruleById(ruleId);
  const causeId = Array.isArray(rule?.sourceCauseId)
    ? rule.sourceCauseId[0]
    : rule?.sourceCauseId;
  return causeId ? metDiagnosticsCauseHref(slug, causeId) : undefined;
}

function nodeIdForReadingScope(readingId: string): string {
  const reading = READINGS.find((row) => row.id === readingId);
  const scope = reading?.scope;
  if (scope?.kind === 'subprocess') return scope.id;
  if (scope?.kind === 'stream') {
    const stream = STREAMS.find((row) => row.id === scope.id);
    if (stream && stream.from !== 'external') return subprocessForNodeId(stream.from) ?? stream.from;
  }
  const tag = TAG_MAPPING.find((row) => row.readingId === readingId);
  if (tag) {
    const tagNodeId = `tag-${tag.role}`;
    if (plantNode(tagNodeId)) return tagNodeId;
    return tag.subprocessId;
  }
  return 'bh-plant';
}

export function samplePointNodeHref(slug: string, samplePointId: string): string {
  const reading =
    (samplePointId === 'SA04'
      ? READINGS.find((row) => row.id === 'rd-spec-zn-grade')
      : undefined) ??
    READINGS.find((row) => row.tag === samplePointId);
  const readingId = reading?.id;
  if (!readingId) {
    return projectSidebarHref(slug, 'plant-model');
  }
  const nodeId = nodeIdForReadingScope(readingId);
  return `${projectSidebarHref(slug, 'plant-model')}?node=${nodeId}&reading=${readingId}`;
}

export function matchedRuleConditionDetail(ruleId: string, caseItem: Case): string | undefined {
  if (caseItem.id !== 'case-zn-recovery' || ruleId !== 'dr-zn-ph') return undefined;
  return 'SMBS 212 g/t vs design 200 · 05:30–06:05 [syn] · now 195 g/t';
}

export function relatedCaseLine(caseItem: Case): string | undefined {
  if (caseItem.id !== 'case-zn-recovery') return undefined;
  const prior = CASES.find((row) => row.id === 'case-zn-ph-response');
  if (!prior) return undefined;
  return 'follows ZN-PH-01 · lime SP raised 06:55 · pH held, recovery has not responded';
}

function learnedReviewLabel(review: FMEAUpdateReview | undefined): string {
  if (!review) return 'awaiting Met review';
  if (review.approvalStatus === 'approved') return 'approved';
  if (review.reviewStatus === 'approved') return 'reviewed · awaiting GM';
  return 'awaiting Met review';
}

/** Rule-review administration moved from MetLead table to the case drawer (landing-reads C4). */
export function drawerRuleReviewAdmin(
  caseItem: Case,
  reviews: readonly FMEAUpdateReview[],
  projectSlug: string,
): { reviewState: string; href: string } | undefined {
  const outcome = CASE_OUTCOMES.find((row) => row.caseId === caseItem.id && row.proposedRuleId);
  if (!outcome) return undefined;
  const review = reviews.find((row) => row.caseOutcomeId === outcome.id);
  return {
    reviewState: learnedReviewLabel(review),
    href: `/projects/${projectSlug}/advisory?tab=approvals`,
  };
}

export function learnedProvenanceForCase(
  caseItem: Case,
  advisory: AdvisorySessionState,
): string | undefined {
  const outcome = outcomeForCase(caseItem.id);
  if (!outcome?.proposedRuleId) return undefined;
  if (!isRuleLive(outcome.proposedRuleId, advisory)) return undefined;
  const promoted = reviewsForCase(caseItem.id, advisory).some(
    (review) =>
      review.proposedChange.ruleId === outcome.proposedRuleId &&
      review.approvalStatus === 'approved' &&
      Boolean(review.approvedBy && review.approvedAt),
  );
  if (!promoted) return undefined;
  const version = liveRuleSet(advisory).version.n;
  return `learned · ${caseItem.code ?? caseItem.id} · rules v${version}`;
}
